import { Request, Response } from "express";
import mongoose from "mongoose";

import { Class, Quiz, QuizAttempt, Student } from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
  school?: {
    id: string;
    name: string;
    isVerified: boolean;
  };
}

/**
 * Quiz Report Controller
 * Handles quiz reporting for teachers and school panel
 */
class QuizReportController {
  /**
   * Get comprehensive quiz report for a specific quiz
   * @route GET /api/v1/school/quizzes/:id/report
   * @access Private (School Admin, Teacher)
   */
  async getQuizReport(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid quiz ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Get quiz details
      const quiz = await Quiz.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("class", "className classCode")
        .populate("semester", "semesterName academicYear");

      if (!quiz) {
        res.status(404).json({
          success: false,
          message: "Quiz not found",
          error: "Quiz not found",
        } as APIResponse);
        return;
      }

      // Get all attempts for this quiz
      const attempts = await QuizAttempt.find({
        quizId: new mongoose.Types.ObjectId(id),
      })
        .populate("student", "fullName studentId profilePicture email")
        .sort({ submittedAt: -1 });

      // Calculate statistics
      const stats = {
        totalAttempts: attempts.length,
        submitted: attempts.filter(a => a.status === "submitted").length,
        graded: attempts.filter(a => a.status === "graded").length,
        inProgress: attempts.filter(a => a.status === "in_progress").length,
        pendingGrading: attempts.filter(
          a => a.status === "submitted" && a.manualGradingRequired,
        ).length,
        averageScore: 0,
        highestScore: 0,
        lowestScore: 0,
        passRate: 0,
      };

      // Calculate score statistics
      const gradedAttempts = attempts.filter(a => a.status === "graded");
      if (gradedAttempts.length > 0) {
        const scores = gradedAttempts.map(
          a => ((a.totalMarksObtained || 0) / quiz.totalMarks) * 100,
        );
        stats.averageScore = Math.round(
          scores.reduce((sum, score) => sum + score, 0) / scores.length,
        );
        stats.highestScore = Math.round(Math.max(...scores));
        stats.lowestScore = Math.round(Math.min(...scores));
        stats.passRate = Math.round(
          (scores.filter(score => score >= 50).length / scores.length) * 100,
        );
      }

      // Format student attempts
      const studentAttempts = attempts.map(attempt => ({
        id: attempt.id,
        student: {
          id: (attempt as any).student?._id,
          fullName: (attempt as any).student?.fullName,
          studentId: (attempt as any).student?.studentId,
          profilePicture: (attempt as any).student?.profilePicture,
          email: (attempt as any).student?.email,
        },
        attemptNumber: attempt.attemptNumber,
        startedAt: attempt.startedAt,
        submittedAt: attempt.submittedAt,
        timeTaken: attempt.timeTaken,
        status: attempt.status,
        totalMarksObtained: attempt.totalMarksObtained,
        autoGradedMarks: attempt.autoGradedMarks,
        percentage: attempt.totalMarksObtained
          ? Math.round((attempt.totalMarksObtained / quiz.totalMarks) * 100)
          : null,
        manualGradingRequired: attempt.manualGradingRequired,
        gradedAt: attempt.gradedAt,
        feedback: attempt.feedback,
      }));

      res.status(200).json({
        success: true,
        message: "Quiz report retrieved successfully",
        data: {
          quiz: {
            id: quiz.id,
            title: quiz.title,
            description: quiz.description,
            totalMarks: quiz.totalMarks,
            duration: quiz.duration,
            deadline: quiz.deadline,
            questionCount: quiz.questions.length,
            class: (quiz as any).class,
            semester: (quiz as any).semester,
            status: quiz.status,
          },
          statistics: stats,
          attempts: studentAttempts,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get quiz report error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve quiz report",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get student quiz report (all quizzes for a specific student)
   * @route GET /api/v1/school/students/:studentId/quiz-report
   * @access Private (School Admin, Teacher)
   */
  async getStudentQuizReport(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { studentId } = req.params;
      const schoolId = req.user?.schoolId;
      const { classId, semesterId } = req.query;

      if (!mongoose.Types.ObjectId.isValid(studentId)) {
        res.status(400).json({
          success: false,
          message: "Invalid student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Verify student exists and belongs to school
      const student = await Student.findOne({
        _id: studentId,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      // Build quiz query
      const quizQuery: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
        status: "published",
      };

      if (classId) {
        quizQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }
      if (semesterId) {
        quizQuery.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      }

      // Get all quizzes
      const quizzes = await Quiz.find({ ...quizQuery })
        .populate("class", "className classCode")
        .populate("semester", "semesterName academicYear")
        .sort({ deadline: -1 });

      // Get student's attempts for these quizzes
      const quizIds = quizzes.map(q => q._id);
      const attempts = await QuizAttempt.find({
        quizId: { $in: quizIds },
        studentId: new mongoose.Types.ObjectId(studentId),
      }).sort({ submittedAt: -1 });

      // Create a map of quiz attempts
      const attemptMap = new Map();
      for (const attempt of attempts) {
        const quizId = attempt.quizId.toString();
        if (!attemptMap.has(quizId)) {
          attemptMap.set(quizId, []);
        }
        attemptMap.get(quizId).push(attempt);
      }

      // Format quiz data with attempts
      const quizReport = quizzes.map(quiz => {
        const quizAttempts = attemptMap.get(quiz._id.toString()) || [];
        const bestAttempt = quizAttempts
          .filter((a: any) => a.status === "graded")
          .sort(
            (a: any, b: any) =>
              (b.totalMarksObtained || 0) - (a.totalMarksObtained || 0),
          )[0];

        // Determine status
        let status = "not_attempted";
        if (quizAttempts.length > 0) {
          status = quizAttempts.some((a: any) => a.status === "graded")
            ? "completed"
            : "in_progress";
        }

        return {
          quiz: {
            id: quiz.id,
            title: quiz.title,
            totalMarks: quiz.totalMarks,
            duration: quiz.duration,
            deadline: quiz.deadline,
            class: (quiz as any).class,
            semester: (quiz as any).semester,
            questionCount: quiz.questions.length,
          },
          attempts: quizAttempts.map((attempt: any) => ({
            id: attempt.id,
            attemptNumber: attempt.attemptNumber,
            startedAt: attempt.startedAt,
            submittedAt: attempt.submittedAt,
            timeTaken: attempt.timeTaken,
            status: attempt.status,
            totalMarksObtained: attempt.totalMarksObtained,
            percentage: attempt.totalMarksObtained
              ? Math.round((attempt.totalMarksObtained / quiz.totalMarks) * 100)
              : null,
            gradedAt: attempt.gradedAt,
            feedback: attempt.feedback,
          })),
          bestScore: bestAttempt
            ? {
                attemptNumber: bestAttempt.attemptNumber,
                marksObtained: bestAttempt.totalMarksObtained,
                percentage: Math.round(
                  (bestAttempt.totalMarksObtained / quiz.totalMarks) * 100,
                ),
                gradedAt: bestAttempt.gradedAt,
              }
            : null,
          totalAttempts: quizAttempts.length,
          status,
        };
      });

      // Calculate overall statistics
      const completedQuizzes = quizReport.filter(
        q => q.status === "completed",
      ).length;
      const totalQuizzes = quizzes.length;
      const averageScore =
        completedQuizzes > 0
          ? Math.round(
              quizReport
                .filter(q => q.bestScore)
                .reduce((sum, q) => sum + (q.bestScore?.percentage || 0), 0) /
                completedQuizzes,
            )
          : 0;

      res.status(200).json({
        success: true,
        message: "Student quiz report retrieved successfully",
        data: {
          student: {
            id: student.id,
            fullName: student.fullName,
            studentId: student.studentId,
            profilePicture: student.profilePicture,
            email: (student.userId as any)?.email,
          },
          summary: {
            totalQuizzes,
            completedQuizzes,
            notAttempted: totalQuizzes - completedQuizzes,
            averageScore,
            totalAttempts: attempts.length,
          },
          quizzes: quizReport,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get student quiz report error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve student quiz report",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get class quiz report (all students' performance for a specific class)
   * @route GET /api/v1/school/classes/:classId/quiz-report
   * @access Private (School Admin, Teacher)
   */
  async getClassQuizReport(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId } = req.params;
      const schoolId = req.user?.schoolId;
      const { quizId } = req.query;

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Verify class exists
      const classItem = await Class.findOne({
        _id: classId,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      }).populate("studentIds", "fullName studentId profilePicture");

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      // Build quiz query
      const quizQuery: any = {
        classId: new mongoose.Types.ObjectId(classId),
        schoolId: new mongoose.Types.ObjectId(schoolId),
        status: "published",
      };

      if (quizId) {
        quizQuery._id = new mongoose.Types.ObjectId(quizId as string);
      }

      // Get quizzes for this class
      const quizzes = await Quiz.find({ ...quizQuery }).sort({ deadline: -1 });

      if (quizzes.length === 0) {
        res.status(200).json({
          success: true,
          message: "No quizzes found for this class",
          data: {
            class: {
              id: classItem.id,
              className: classItem.className,
              classCode: classItem.classCode,
            },
            quizzes: [],
            students: [],
          },
        } as APIResponse);
        return;
      }

      // Get all attempts for these quizzes
      const quizIds = quizzes.map(q => q._id);
      const attempts = await QuizAttempt.find({
        quizId: { $in: quizIds },
      })
        .populate("student", "fullName studentId profilePicture")
        .sort({ submittedAt: -1 });

      // Create student performance map
      const studentPerformance = new Map();
      const students = classItem.studentIds as any[];

      for (const student of students) {
        studentPerformance.set(student._id.toString(), {
          student: {
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            profilePicture: student.profilePicture,
          },
          quizzes: {},
          summary: {
            totalQuizzes: quizzes.length,
            completed: 0,
            averageScore: 0,
            totalAttempts: 0,
          },
        });
      }

      // Populate quiz attempts for each student
      for (const attempt of attempts) {
        const studentId = (attempt as any).student._id.toString();
        const quizId = attempt.quizId.toString();

        if (studentPerformance.has(studentId)) {
          const studentData = studentPerformance.get(studentId);

          if (!studentData.quizzes[quizId]) {
            studentData.quizzes[quizId] = [];
          }

          studentData.quizzes[quizId].push({
            attemptNumber: attempt.attemptNumber,
            submittedAt: attempt.submittedAt,
            status: attempt.status,
            marksObtained: attempt.totalMarksObtained,
            percentage: attempt.totalMarksObtained
              ? Math.round(
                  (attempt.totalMarksObtained /
                    (quizzes.find(q => q._id.toString() === quizId)
                      ?.totalMarks || 1)) *
                    100,
                )
              : null,
          });

          studentData.summary.totalAttempts++;
          if (attempt.status === "graded") {
            studentData.summary.completed++;
          }
        }
      }

      // Calculate average scores
      for (const [, studentData] of studentPerformance) {
        const completedQuizzes = Object.values(studentData.quizzes).filter(
          (attempts: any) => attempts.some((a: any) => a.status === "graded"),
        ).length;

        if (completedQuizzes > 0) {
          let totalScore = 0;
          for (const attempts of Object.values(studentData.quizzes)) {
            const bestAttempt = (attempts as any[])
              .filter((a: any) => a.status === "graded")
              .sort(
                (a: any, b: any) => (b.percentage || 0) - (a.percentage || 0),
              )[0];
            totalScore += bestAttempt?.percentage || 0;
          }
          studentData.summary.averageScore = Math.round(
            totalScore / completedQuizzes,
          );
        }
      }

      // Format quiz summary
      const quizSummary = quizzes.map(quiz => ({
        id: quiz.id,
        title: quiz.title,
        totalMarks: quiz.totalMarks,
        deadline: quiz.deadline,
        attemptCount: attempts.filter(
          a => a.quizId.toString() === quiz._id.toString(),
        ).length,
        averageScore: Math.round(
          attempts
            .filter(
              a =>
                a.quizId.toString() === quiz._id.toString() &&
                a.status === "graded",
            )
            .reduce(
              (sum, a) =>
                sum + ((a.totalMarksObtained || 0) / quiz.totalMarks) * 100,
              0,
            ) /
            Math.max(
              attempts.filter(
                a =>
                  a.quizId.toString() === quiz._id.toString() &&
                  a.status === "graded",
              ).length,
              1,
            ),
        ),
      }));

      res.status(200).json({
        success: true,
        message: "Class quiz report retrieved successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            totalStudents: students.length,
          },
          quizzes: quizSummary,
          students: [...studentPerformance.values()],
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get class quiz report error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve class quiz report",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get overall quiz statistics for school
   * @route GET /api/v1/school/quiz-statistics
   * @access Private (School Admin)
   */
  async getSchoolQuizStatistics(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      const { semesterId, classId } = req.query;

      // Build quiz query
      const quizQuery: any = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (semesterId) {
        quizQuery.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );
      }
      if (classId) {
        quizQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }

      // Get all quizzes
      const quizzes = await Quiz.find(quizQuery);
      const quizIds = quizzes.map(q => q._id);

      // Get all attempts
      const attempts = await QuizAttempt.find({
        quizId: { $in: quizIds },
      });

      // Calculate statistics
      const stats = {
        totalQuizzes: quizzes.length,
        publishedQuizzes: quizzes.filter(q => q.status === "published").length,
        draftQuizzes: quizzes.filter(q => q.status === "draft").length,
        closedQuizzes: quizzes.filter(q => q.status === "closed").length,
        totalAttempts: attempts.length,
        submittedAttempts: attempts.filter(a => a.status === "submitted")
          .length,
        gradedAttempts: attempts.filter(a => a.status === "graded").length,
        pendingGrading: attempts.filter(
          a => a.status === "submitted" && a.manualGradingRequired,
        ).length,
        averageScore: 0,
        passRate: 0,
      };

      // Calculate score statistics
      const gradedAttempts = attempts.filter(a => a.status === "graded");
      if (gradedAttempts.length > 0) {
        const scores = gradedAttempts.map(attempt => {
          const quiz = quizzes.find(
            q => q._id.toString() === attempt.quizId.toString(),
          );
          return quiz
            ? ((attempt.totalMarksObtained || 0) / quiz.totalMarks) * 100
            : 0;
        });

        stats.averageScore = Math.round(
          scores.reduce((sum, score) => sum + score, 0) / scores.length,
        );
        stats.passRate = Math.round(
          (scores.filter(score => score >= 50).length / scores.length) * 100,
        );
      }

      res.status(200).json({
        success: true,
        message: "School quiz statistics retrieved successfully",
        data: {
          statistics: stats,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get school quiz statistics error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve quiz statistics",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }
}

export const quizReportController = new QuizReportController();
